import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js'

import { TGetItemFn } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { getPath } from '@/utils/urlUtils'

import { Spinner } from '@/components/spinner'

import { ApiService } from '@/api'
import { TApprovePayPalPurchasePayload, TCreatePayPalPurchasePayload } from '@/api/types'
import { emailKey, tokenHeader } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import {
  trackLTPPaymentClick,
  trackLTPPaymentInfoAdded,
  trackLTPPurchaseStarted,
} from '@/events/events'

import { TLTPPayPalButton } from './types'

function preparePayPalPayload(
  getItem: TGetItemFn,
  sessionContext: ISessionContext,
  intent_id: string
): TApprovePayPalPurchasePayload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  const email = getItem('testEmail')
  if (email) {
    console.log(`testEmail '${email} detected`)
  }

  return {
    session_id: sessionId,
    test_email: email,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    purchase_intent_id: intent_id,
  }
}

export const LTPPayPalButtonV2 = ({
  pageName,
  paypalButtonConfigStyle,
  contentDetails,
  setItem,
  getItem,
  config,
  customTagline,
  successRedirectTo,
}: TLTPPayPalButton) => {
  const { authUser } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const [{ isPending }] = usePayPalScriptReducer()
  const [isPaypalInitialCheckoutEventIsSent, setPaypalInitialCheckoutEventIsSent] = useState(false)
  const { currency, price } = contentDetails
  const semaphore = useRef<boolean>(false)
  const orderIntentId = useRef<string | undefined>(undefined)

  const createOrder = useCallback(async () => {
    if (semaphore.current) {
      return Promise.reject()
    }

    const payload: TCreatePayPalPurchasePayload = {
      session_id: sessionContext.sessionId,
      page_set_id: config.PAGE_SET_ID,
      internal_content_price_id: contentDetails.internalPriceId,
    }
    semaphore.current = true
    try {
      const response = await ApiService.createPayPalPurchase(payload)

      semaphore.current = false

      orderIntentId.current = response.data.intent_id
      return response.data.order_id
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      console.log('createOrder err', e)
      orderIntentId.current = undefined
      semaphore.current = false

      navigate(getPath(PageName.ltpPaymentFailed))
      return Promise.reject(e)
    }
  }, [config, contentDetails, navigate, orderIntentId, sessionContext])

  const onApprove = useCallback(async () => {
    console.log('orderIntentId', orderIntentId.current)

    if (!orderIntentId.current) {
      navigate(getPath(PageName.ltpPaymentFailed))
      return
    }
    trackLTPPurchaseStarted(
      pageName,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      contentDetails.internalPriceId,
      PaymentMethod.PAYPAL,
      PaymentPlatform.PAYPAL,
      sessionContext
    )
    setItem(PageName.paymentScreen, 'paypal')

    const payload = preparePayPalPayload(getItem, sessionContext, orderIntentId.current)

    try {
      resetContactStateCheckTimeout(setItem)
      const response = await ApiService.approvePayPalPurchase(payload)
      console.log('purchase', response)

      authUser(response.data.contact_id, response.headers[tokenHeader], response.data.email)
      navigate(getPath(successRedirectTo))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      semaphore.current = false
      console.log('purchase error', e)

      if (e.response.data?.email) {
        setItem(emailKey, e.response.data.email)
      }

      if (e.response?.status === 409) {
        navigate(getPath(successRedirectTo))
      } else {
        navigate(getPath(PageName.ltpPaymentFailed))
      }
    }
  }, [
    navigate,
    config,
    contentDetails,
    sessionContext,
    orderIntentId,
    authUser,
    getItem,
    pageName,
    setItem,
    successRedirectTo,
  ])

  const handlePaypalButtonCLick = async () => {
    if (!isPaypalInitialCheckoutEventIsSent) {
      setItem('ltpPaymentMethod', PaymentMethod.PAYPAL)
      setItem('ltpPaymentPlatform', PaymentPlatform.PAYPAL)
      trackLTPPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext,
        null,
        [],
        ['paypal']
      )
      trackLTPPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        PaymentMethod.PAYPAL,
        PaymentPlatform.PAYPAL,
        sessionContext
      )
      setPaypalInitialCheckoutEventIsSent(true)
    }
  }

  return (
    <>
      {customTagline && <div className="paypal-button-custom-tagline">{customTagline}</div>}
      {isPending && <Spinner />}
      <PayPalButtons
        style={{ ...paypalButtonConfigStyle.style }}
        disabled={false}
        forceReRender={[price, currency]}
        fundingSource={undefined}
        onClick={handlePaypalButtonCLick}
        onError={() => {}}
        createOrder={createOrder}
        onApprove={onApprove}
      />
    </>
  )
}
